import React from 'react';
import placeholder from '../assets/placeholder.svg';
import { resolveMediaUrl, isMediaObjectKey, peekCachedMediaUrl } from '../utils/mediaApi';

interface Props extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackSrc?: string;
}

const ImageWithFallback: React.FC<Props> = ({ src, alt, fallbackSrc = placeholder, onError, ...rest }) => {
  const [imgSrc, setImgSrc] = React.useState<string | undefined>(() => {
    const initial = src as string | undefined;
    return initial && isMediaObjectKey(initial) ? peekCachedMediaUrl(initial) : initial;
  });

  React.useEffect(() => {
    const next = src as string | undefined;
    let cancelled = false;

    if (!next) {
      setImgSrc(undefined);
      return;
    }

    if (!isMediaObjectKey(next)) {
      setImgSrc(next);
      return;
    }

    const cached = peekCachedMediaUrl(next);
    if (cached) {
      setImgSrc(cached);
      return;
    }

    setImgSrc(undefined);
    resolveMediaUrl(next)
      .then((resolved) => {
        if (!cancelled) setImgSrc(resolved);
      })
      .catch(() => {
        if (!cancelled) setImgSrc(fallbackSrc);
      });

    return () => {
      cancelled = true;
    };
  }, [src, fallbackSrc]);

  return (
    <img
      {...rest}
      src={imgSrc || fallbackSrc}
      alt={alt || ''}
      onError={(e) => {
        if (imgSrc && imgSrc !== fallbackSrc) {
          setImgSrc(fallbackSrc);
        }
        if (onError) onError(e);
      }}
    />
  );
};

export default ImageWithFallback;
